<template>
	<div>
		<!-- delete dialog -->
		<bee-delete-dialog
			v-model="deleteDialog"
			moduleType="offers"
			:ids="deleteItemId"
			@on-delete="fetchOffers"
		>
			{{$t('are-you-sure-you-want-to')}} 
			<span class="text-uppercase error--text">{{$t('delete')}}</span> 
			{{$t('Offers.the-offer')}}
		</bee-delete-dialog>

		<!-- add/edit dialog -->
		<v-dialog v-model="dialog" width="600" persistent>
			<v-form ref="form" @submit.prevent="submit">
				<v-card class="rounded-medium">
					<v-card-title
						class="justify-center text-uppercase text-h6"
						:class="{ 'mb-3': !multiLangInputs, 'pb-2': multiLangInputs }"
					>
						{{ editItemId ? $t('edit') : $t('add')}} {{$t('Offers.offer')}}
					</v-card-title>
					<v-card-text class="pb-2">
						<!-- lang tabs -->
						<v-lang-tabs
							v-if="multiLangInputs"
							v-model="lang"
							:disable-non-selected="dialogLoading"
							background-color="bgColor"
							class="mb-4"
							@change="updateFieldsFromDraft"
						/>

						<!-- inputs -->
						<v-row dense class="mt-0">
							<!-- name -->
							<v-col sm="4" cols="12">
								<v-text-field
									v-model="name"
									:label="$t('inputs.name')"
									outlined
									dense
									class="rounded-small"
									hide-details="auto"
									:rules="rules.required"
								></v-text-field>
							</v-col>

							<!-- from date -->
							<v-col sm="4" cols="6">
								<v-menu
									v-model="fromDateMenu"
									:close-on-content-click="false"
									:nudge-right="40"
									transition="scale-transition"
									offset-y
									min-width="auto"
								>
									<template v-slot:activator="{ on, attrs }">
										<v-text-field
											v-model="fromDate"
											:label="$t('inputs.from-date')"
											prepend-inner-icon="mdi-calendar"
											outlined
											dense
											class="rounded-small"
											hide-details="auto"
											readonly
											clearable
											v-bind="attrs"
											v-on="on"
										></v-text-field>
									</template>
									<v-date-picker
										v-model="fromDate"
										@input="fromDateMenu = false"
									></v-date-picker>
								</v-menu>
							</v-col>

							<!-- to date -->
							<v-col sm="4" cols="6">
								<v-menu
									v-model="toDateMenu"
									:close-on-content-click="false"
									:nudge-right="40"
									transition="scale-transition"
									offset-y
									min-width="auto"
								>
									<template v-slot:activator="{ on, attrs }">
										<v-text-field
											v-model="toDate"
											:label="$t('inputs.to-date')"
											prepend-inner-icon="mdi-calendar"
											outlined
											dense
											class="rounded-small"
											hide-details="auto"
											readonly
											clearable
											:rules="[() => moment(toDate).isAfter(fromDate)]"
											v-bind="attrs"
											v-on="on"
										></v-text-field>
									</template>
									<v-date-picker
										v-model="toDate"
										@input="toDateMenu = false"
									></v-date-picker>
								</v-menu>
							</v-col>

							<!-- offer products -->
							<v-col cols="12">
								<v-autocomplete
									v-model="offerProducts"
									:label="$t('inputs.offer-products')"
									:items="products"
									item-value="id"
									item-text="name"
									chips
									small-chips
									deletable-chips
									multiple
									outlined
									dense
									class="rounded-small"
									hide-details="auto"
									:rules="rules.required"
								></v-autocomplete>
							</v-col>

							<!-- discount value -->
							<v-col cols="12" class="d-flex flex-wrap algin-center">
								<v-radio-group
									v-model="discountType"
									row
									dense
									hide-details
									class="ma-0 pt-2"
								>
									<template v-slot:label>
										<span class="text-body-1">{{$t('inputs.discount')}}</span>
									</template>
									<v-radio :label="$t('inputs.percent')" :value="0"></v-radio>
									<v-radio :label="$t('inputs.fixed-value')" :value="1"></v-radio>
								</v-radio-group>

								<v-text-field
									v-model="discount"
									:label="$t('inputs.discount')"
									outlined
									dense
									class="rounded-small"
									hide-details="auto"
									:rules="rules.required"
									:append-icon="discountType === 0 ? 'mdi-percent' : null"
								></v-text-field>
							</v-col>

							<!-- apply on up-selling -->
							<v-col v-if="useUpSellingForm" sm="5" cols="12">
								<v-switch
									v-model="applyOnUpSelling"
									:label="$t('inputs.apply-on-up-selling')"
									hide-details
									class="mt-0 pt-0"
								/>
							</v-col>
						</v-row>
					</v-card-text>
					<v-card-actions>
						<v-spacer/>
						<v-btn
							type="submit"
							color="primary"
							:loading="dialogLoading"
							:disabled="dialogLoading"
							class="rounded-small"
						>{{$t('save')}}</v-btn>
						<v-btn
							:disabled="dialogLoading"
							class="rounded-small"
							@click="dialog = false"
						>{{$t('cancel')}}</v-btn>
					</v-card-actions>
				</v-card>
			</v-form>
		</v-dialog>

		<!-- filters -->
		<v-card class="rounded-medium mb-3">
			<v-card-text class="pa-2">
				<v-row class="align-center">
					<!-- add -->
					<v-col md="8" cols="12" class="text-sm-start text-center">
						<v-btn
							dark
							color="darkGreen"
							class="rounded-small"
							@click="dialog = true"
						>
							<v-icon>mdi-plus</v-icon>
							{{$t('Offers.add-new-offer')}}
						</v-btn>
					</v-col>
					<!-- search -->
					<v-col md="4" cols="12" class="d-flex flex-sm-row flex-column align-center text-md-start text-center">
						<v-text-field
							v-model="search"
							:label="$t('search')"
							hide-details
							outlined
							dense
							prepend-inner-icon="mdi-magnify"
							class="rounded-medium align-self-stretch me-sm-6 mb-sm-0 mb-5"
						></v-text-field>
						<v-btn
							:color="isFiltered ? null : 'primary'"
							class="rounded-small"
							@click="isFiltered = !isFiltered"
						>
							{{isFiltered ? $t('cancel') : $t('search')}}
						</v-btn>
					</v-col>
				</v-row>
			</v-card-text>
		</v-card>
		
		<!-- table -->
		<v-data-table
			:headers="customizedHeaders"
			:items="loading ? [] : filteredSets"
			:loading="loading"
			dense
			:items-per-page="15"
			class="elevation-3 rounded-medium"
		>
			<!-- actions header -->
			<template v-slot:header.actions="{ header }">
				<div class="d-flex align-center" :class="`justify-${header.align}`">
					<span class="px-0" :class="header.class">{{header.text}}</span>
					<v-btn
						small
						icon
						@click="$eventBus.$emit(
							'headers-control',
							allHeaders.filter(c => !c.hide),
							customizedHeaders,
							(newHeaders) => {
								$eStorage.l.offersHeaders = newHeaders;
							}
						)"
					>
						<v-icon size="18">mdi-cog</v-icon>
					</v-btn>
				</div>
			</template>

			<!-- products -->
			<template v-slot:item.products="{ item }">
				{{item.products.map(c => getProductsById(c.id).name).join(', ')}}
			</template>

			<!-- actions -->
			<template v-slot:item.actions="{ item }">
				<tooltip :text="$t('tooltip.edit')">
					<v-btn
						color="blue darken-4"
						dark
						small
						class="px-1 flex-shrink-1"
						min-width="20px"
						@click="dialog = true; editItemId = item.id"
					><v-icon>mdi-pencil</v-icon></v-btn>
				</tooltip>
				
				<tooltip :text="$t('tooltip.delete')">
					<v-btn
						color="red darken-1"
						dark
						small
						class="ms-1 px-1"
						min-width="20px"
						@click="deleteDialog = true; deleteItemId = item.id"
					>
						<v-icon>mdi-delete</v-icon>
					</v-btn>
				</tooltip>
			</template>
		</v-data-table>
	</div>
</template>

<script>
import { mapGetters, mapState } from 'vuex';
import { defaultLang, langs, multiLangInputs } from '@/configs/translates.config';
import rules from '@/helpers/validation rules'
import moment from 'moment';
import langsDraft from '@/mixins/langs-draft';
import { useUpSellingForm } from '@/configs/global.config';

export default {
	name: 'Offers',
	mixins: [langsDraft],

	data: () => ({
		dialog: false,
		editItemId: null,
		
		deleteDialog: false,
		deleteItemId: null,

		fromDateMenu: null,
		toDateMenu: null,
		
		discountType: 0,

		// filter
		search: null,
		isFiltered: false,

		// configs
		multiLangInputs,
		useUpSellingForm,

		// helpers
		loading: null,
		dialogLoading: null,
		lang: defaultLang,
		rules,
		moment,

		// draft for languages 
		draft: {},
		draftNames: ['name'],

		// data
		name: null,
		offerProducts: null,
		fromDate: null,
		toDate: null,
		discount: null,
		applyOnUpSelling: false
	}),

	watch: {
		dialog(val) {
			if (val) {
				if (this.editItemId) {
					if (multiLangInputs) {
						this.fetchOffer();
					} else {
						const item = this.getOfferBySlug(this.editItemId);
						this.name = item.name;
						this.offerProducts = item.offerProducts;
						this.fromDate = item.fromDate;
						this.toDate = item.toDate;
					}
				} else {
					this.discountType = 0;
				}
			} else {
				this.$refs.form.reset();
				this.resetDraft();
				this.editItemId = null;
				this.lang = defaultLang;
			}
		}
	},

	computed: {
		...mapState({
			products: state => state.products.products,
			offers: state => state.offers.offers,
		}),

		...mapGetters({
			getOfferBySlug: 'offers/getOfferBySlug',
			getProductsById: 'products/getProductsById'
		}),

		allHeaders() {
			const headers = [
				{
					text: this.$t('headers.name'),
					value: 'name',
					align: 'center',
					class: 'primary--text',
					cellClass: 'primary--text',
					noRemove: true,
				},
				{
					text: this.$t('headers.max-beneficiaries-number'),
					value: 'offer-code',
					align: 'center',
					notInDefaultView: true
				},
				{
					text: this.$t('headers.products'),
					value: 'products',
					align: 'center',
					notInDefaultView: true
				},
				{
					text: this.$t('headers.from-date'),
					value: 'from-date',
					align: 'center',
				},
				{
					text: this.$t('headers.to-date'),
					value: 'to-date',
					align: 'center',
				},
				{
					text: this.$t('headers.discount'),
					value: 'discount',
					align: 'center',
				},
				{
					text: this.$t('headers.actions'),
					value: 'actions',
					align: 'center',
					sortable: false,
					noRemove: true,
					fixed: true
				},
			]

			for (let i = 0; i < headers.length; i++) {
				headers[i].class = headers[i].class ? headers[i].class + ' px-2' : 'px-2'
				headers[i].cellClass = headers[i].cellClass ? headers[i].cellClass + ' px-2' : 'px-2'
			}

			return headers
		},

		customizedHeaders() {
			let headers = [];
			if (this.$eStorage.l.offersHeaders.length) {
				headers = this.$eStorage.l.offersHeaders;
			} else {
				headers = this.allHeaders.filter(c => !c.notInDefaultView);
			}
			return headers.filter(c => !c.hide);
		},

		filteredSets() {
			return this.filterByName(this.isFiltered, this.offers, this.search);
		}
	},

	methods: {
		async submit() {
			if (this.$refs.form.validate()) {
				this.dialogLoading = true;

				const promises = [];
				let itemData = null;

				const submitRequest = (
					isDefaultOne,
					{ name, lang },
					id = this.editItemId
				) => {
					const actionName = id ? 'update' : 'create';

					return this.$store.dispatch(`offers/${actionName}`, {
						id: id,
						offerProducts: this.offerProducts,
						fromDate: this.fromDate,
						toDate: this.toDate,
						discount: this.discount,
						name: name,
						acceptLanguage: lang
					}).then((data) => data)
				}

				const filteredLangs = langs.filter(c => multiLangInputs || c.key === defaultLang);
				for (let index = 0; index < filteredLangs.length; index++) {
					const lang = filteredLangs[index].key;

					const name = this.draft.name[lang];

					const dataDueLang = { name, lang };

					if (name) {
						if (index === 0) {
							await submitRequest(index === 0, dataDueLang).then(data => { itemData = data });
						} else {
							promises.push(submitRequest(false, dataDueLang, itemData.id));
						}
					}
				}

				const successHandler = () => {
					const message =
						this.editItemId
							? this.$t('$-is-updated-successfully', { name: this.$t('Offers.offer') })
							: this.$t('$-is-created-successfully', { name: this.$t('Offers.offer') });
					this.$eventBus.$emit('show-snackbar', false, message);
					
					this.fetchCoupons();
					this.dialog = false;
				};
				const errorHandler = () => {
					if (itemData) {
						this.editItemId = itemData.id;
					}
				}

				Promise.all(promises).then(successHandler).catch(errorHandler).finally(() => {
					this.dialogLoading = false;
				})
			}
		},

		fetchOffers(isFirstLoad) {
			this.loading = true;
			this.$store.dispatch('offers/fetchAll')
				.finally(() => {
					if (!isFirstLoad) this.loading = false;
				})
		},

		fetchOffer() {
			this.dialogLoading = true;
			const slug = this.getOfferBySlug(this.editItemId).slug;
			this.$store.dispatch('offers/fetchBySlug', { slug, acceptLanguage: this.lang })
				.then((data) => {
					this.name = data.name;
				})
				.finally(() => {
					this.dialogLoading = false;
				})
		}
	},

	created() {
		this.loading = true;
		Promise.all([
			this.fetchOffers(),
			this.$store.dispatch('products/fetchAll', {}),
		]).finally(() => {
			this.loading = false;
		})
	}
}
</script>

<style>

</style>